import { useState } from 'react';
import { ApiFailure, api } from '../lib/api';

/**
 * 改密码。
 *
 * ★ 首次登录必须改初始密码 —— 而在这一屏出现之前,管理端撞上这件事的
 *   表现是「登录成功之后立刻又被弹回登录页」:服务端回的是
 *   `PASSWORD_CHANGE_REQUIRED`,前端把所有 401 一律当成掉线。
 *   **每一个新部署的第一次登录都会踩到**,而且看起来像密码错了。
 *
 * ★ 也给主动改密留了入口(传 onCancel),否则想换密码只能找人重置 ——
 *   而重置意味着把一个初始密码在微信或电话里念一遍。
 */
export function ChangePassword({
  onDone,
  onCancel,
  embedded = false,
}: {
  onDone: () => void;
  /** 主动改时给一个退路;首次登录强制改的那条路不传 */
  onCancel?: () => void;
  /**
   * 嵌在设置页里用。
   *
   * ★ 首次登录那条路是**整屏**的:那时候人除了改密码没有别的事可做,
   *   给他一屏别的东西只会分散注意力。而在设置里它只是一块 ——
   *   还顶着 min-h-dvh 的话,那一屏会莫名其妙地空出一大截。
   */
  embedded?: boolean;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 10;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || mismatch || tooShort || !current || !next) return;
    setBusy(true);
    setError(null);
    try {
      await api('/auth/password/change', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : '修改失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={
        embedded
          ? ''
          : 'min-h-dvh flex flex-col justify-center px-6 bg-slate-50 dark:bg-slate-900'
      }
    >
      <div className={embedded ? 'w-full' : 'mx-auto w-full max-w-sm'}>
        {!embedded && (
          <>
            <h1 className="mb-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
              {onCancel ? '修改密码' : '设置新密码'}
            </h1>
            <p className="mb-6 text-slate-500">
              {onCancel ? '改完之后其它设备要重新登录' : '首次登录需要修改初始密码'}
            </p>
          </>
        )}

        <form onSubmit={submit} className="space-y-4">
          <Field label="当前密码" value={current} onChange={setCurrent} autoFocus />
          <Field label="新密码(至少 10 位)" value={next} onChange={setNext} />
          <Field label="确认新密码" value={confirm} onChange={setConfirm} />

          {/* ★ 两条都在输入时就说,而不是等提交之后才报 */}
          {tooShort && <p className="text-sm text-amber-600">还差 {10 - next.length} 位</p>}
          {mismatch && <p className="text-sm text-red-600">两次输入不一样</p>}
          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || mismatch || tooShort || !current || !next}
            className="w-full rounded-lg bg-brand-600 px-3 py-3 font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '提交中…' : '确定'}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full rounded-lg border border-slate-300 px-3 py-3 text-slate-700
                         dark:border-slate-600 dark:text-slate-200"
            >
              取消
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-slate-600 dark:text-slate-300">{label}</span>
      <input
        type="password"
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900
                   dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
      />
    </label>
  );
}
